import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetTransactionPaymentsQuery,
    type TransactionPaymentsFilterInput,
    PaymentIntentState,
    PaymentMethodName,
    PaymentMethodProvider,
} from 'data/__generated__';
import { dateRangeColumn, entityColumn, multiSelectColumn, textColumn } from 'components/DataTable';
import { DetailLink } from 'components/DetailsView/DetailLink';
import { anyStateSiteSource, anyStateUserSource } from 'data/entity-sources/sources';
import { getPaymentMethodLabel } from 'helpers/paymentMethods';
import { formatPrice } from 'utils/price';

import { PaymentMethodStatusTag } from 'pages/Transactions/components/PaymentMethodStatusTag';
import { PAYMENT_METHOD_STATUS_LABELS } from 'pages/Transactions/constants/states';

import { getPaymentInstrumentId, getPaymentMethodTargetPath, PaymentIdLink } from '../components/PaymentIdLink';

export type PaymentRow = GetTransactionPaymentsQuery['transactionPayments']['transactionPayments'][number];

// Libellés et produits sont tronqués court : les colonnes d'identifiants, le cœur de la vue, doivent tenir à l'écran.
const COMPACT_TEXT_LENGTH = 24;

// `onSelectId` : un clic sur l'identifiant d'une charge filtre la table sur elle, ce qui fait apparaître les autres
// transactions du même batch.
export const buildPaymentsColumns = ({
    onSelectId,
}: {
    onSelectId: (id: string) => void;
}): ColumnDef<PaymentRow, unknown>[] => [
    // Un paiement n'a pas de date propre : c'est celle de la transaction (l'achat, pas le passage du batch).
    dateRangeColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'createdAt',
        accessorFn: (row) => row.transaction.createdAt,
        header: 'Date',
        filterKey: 'createdAt',
        defaultVisible: true,
    }),
    entityColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'userFullName',
        accessorFn: (row) => row.transaction.userFullName,
        header: 'Utilisateur',
        filterKey: 'userIds',
        source: anyStateUserSource,
        defaultVisible: true,
        cell: ({ row }) => (
            <DetailLink
                path="user"
                name={row.original.transaction.userFullName}
                value={row.original.transaction.userId}
            />
        ),
    }),
    entityColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'siteName',
        accessorFn: (row) => row.transaction.siteName,
        header: 'Site',
        filterKey: 'siteIds',
        source: anyStateSiteSource,
        cell: ({ row }) => (
            <DetailLink path="site" name={row.original.transaction.siteName} value={row.original.transaction.siteId} />
        ),
    }),
    multiSelectColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'paymentMethodName',
        accessorFn: (row) => row.paymentMethod.paymentMethodName,
        header: 'Moyen',
        filterKey: 'paymentMethodNames',
        defaultVisible: true,
        options: Object.values(PaymentMethodName).map((name) => ({ value: name, label: getPaymentMethodLabel(name) })),
        cell: ({ row }) => getPaymentMethodLabel(row.original.paymentMethod.paymentMethodName),
    }),
    textColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'name',
        accessorFn: (row) => row.paymentMethod.name ?? '',
        header: 'Libellé',
        defaultVisible: true,
        truncateLength: COMPACT_TEXT_LENGTH,
    }),
    multiSelectColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'provider',
        accessorFn: (row) => row.paymentMethod.provider ?? '-',
        header: 'Fournisseur',
        filterKey: 'providers',
        options: Object.values(PaymentMethodProvider).map((provider) => ({ value: provider, label: provider })),
        cell: ({ row }) => row.original.paymentMethod.provider ?? '-',
    }),
    multiSelectColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'status',
        accessorFn: (row) => row.paymentMethod.status,
        header: 'Statut',
        filterKey: 'statuses',
        defaultVisible: true,
        options: Object.values(PaymentIntentState).map((status) => ({
            value: status,
            label: PAYMENT_METHOD_STATUS_LABELS[status],
        })),
        renderOption: (option) => <PaymentMethodStatusTag status={option.value as PaymentIntentState} />,
        cell: ({ row }) => <PaymentMethodStatusTag status={row.original.paymentMethod.status} />,
    }),
    textColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'amountUsed',
        accessorFn: (row) => row.paymentMethod.amountUsed,
        header: 'Montant',
        defaultVisible: true,
        cell: ({ row }) => formatPrice(row.original.paymentMethod.amountUsed),
    }),
    // Le crédit, la carte, la subvention… : le même sur toutes les transactions qu'il a payées.
    textColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'paymentMethodId',
        accessorFn: (row) => row.paymentMethod._id,
        header: 'ID moyen',
        defaultVisible: true,
        // Vers la fiche de son type ; un badge pro, qui n'en a pas, filtre la table sur place.
        cell: ({ row }) => (
            <PaymentIdLink
                id={getPaymentInstrumentId(row.original.paymentMethod)}
                to={getPaymentMethodTargetPath(row.original.paymentMethod) ?? undefined}
                onSelect={onSelectId}
            />
        ),
    }),
    // La charge chez le prestataire : un PaymentIntent Stripe couvre toutes les transactions d'un batch.
    textColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'providerTransactionId',
        accessorFn: (row) => row.paymentMethod.providerTransactionId ?? '',
        header: 'ID paiement',
        defaultVisible: true,
        cell: ({ row }) => (
            <PaymentIdLink id={row.original.paymentMethod.providerTransactionId} onSelect={onSelectId} />
        ),
    }),
    textColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'productName',
        accessorFn: (row) => row.transaction.productName,
        header: 'Produit',
        defaultVisible: true,
        cell: ({ row }) => {
            const { _id, productName } = row.original.transaction;
            const name =
                productName.length > COMPACT_TEXT_LENGTH
                    ? `${productName.slice(0, COMPACT_TEXT_LENGTH)}…`
                    : productName;
            return <DetailLink path="transaction" name={name} value={_id} />;
        },
    }),
    textColumn<PaymentRow, TransactionPaymentsFilterInput>({
        id: 'explanation',
        accessorFn: (row) => row.paymentMethod.explanation ?? '',
        header: 'Explication',
    }),
];
